/**
 * Maps Firebase Auth error codes to user-facing messages.
 *
 * Firebase surfaces errors as `FirebaseError` with a `code` like
 * `auth/invalid-credential`, and a `message` that includes the raw
 * `Firebase: Error (auth/...).` text. Neither is safe to show directly,
 * so every error shown in the UI should pass through {@link getFriendlyAuthErrorMessage}.
 */
import type { SignUpFailureReason } from '@/analytics';

const FIREBASE_MESSAGES: Record<string, string> = {
  'auth/account-exists-with-different-credential':
    'An account already exists with this email. Sign in with your original method to continue.',
  'auth/invalid-credential': 'Incorrect email or password.',
  'auth/invalid-email': 'That email address is not valid.',
  'auth/invalid-login-credentials': 'Incorrect email or password.',
  'auth/user-disabled': 'This account has been disabled. Contact support for help.',
  'auth/user-not-found': 'No account found with that email.',
  'auth/wrong-password': 'Incorrect email or password.',
  'auth/email-already-in-use': 'An account with this email already exists.',
  'auth/weak-password': 'Password is too weak. Use at least 6 characters.',
  'auth/operation-not-allowed': 'This sign-in method is currently unavailable.',
  'auth/popup-closed-by-user': 'Sign-in window was closed before completing.',
  'auth/popup-blocked': 'Your browser blocked the sign-in popup. Allow popups and try again.',
  'auth/cancelled-popup-request': 'Sign-in was cancelled.',
  'auth/network-request-failed': 'Network error. Check your connection and try again.',
  'auth/too-many-requests': 'Too many attempts. Please wait a moment and try again.',
  'auth/requires-recent-login': 'Please sign in again to continue.',
  'auth/invalid-phone-number': 'That phone number is not valid.',
  'auth/missing-phone-number': 'Please enter a phone number.',
  'auth/invalid-verification-code': 'The verification code is incorrect.',
  'auth/code-expired': 'The verification code has expired. Request a new one.',
  'auth/missing-verification-code': 'Please enter the verification code.',
  'auth/quota-exceeded': 'Too many codes sent. Please try again later.',
  'auth/captcha-check-failed': 'CAPTCHA check failed. Refresh the page and try again.',
};

const DEFAULT_MESSAGE = 'Something went wrong. Please try again.';

const FIREBASE_CODE_REGEX = /auth\/[a-z0-9-]+/i;

/**
 * Extracts an `auth/...` code from a Firebase error. Works with both
 * `FirebaseError` objects (which expose `.code`) and plain strings
 * produced by wrapping the error in something like `new Error(message)`.
 */
function extractFirebaseCode(input: unknown): string | null {
  if (!input) return null;

  if (typeof input === 'object') {
    const maybeCode = (input as { code?: unknown }).code;
    if (typeof maybeCode === 'string' && maybeCode.startsWith('auth/')) {
      return maybeCode;
    }
    const maybeMessage = (input as { message?: unknown }).message;
    if (typeof maybeMessage === 'string') {
      return extractFirebaseCode(maybeMessage);
    }
    return null;
  }

  if (typeof input === 'string') {
    const match = input.match(FIREBASE_CODE_REGEX);
    return match ? match[0] : null;
  }

  return null;
}

/**
 * Public accessor for the extracted `auth/...` code, for flows that branch on
 * the specific failure (e.g. signup→login force-fallback on
 * `auth/email-already-in-use`) rather than just displaying a message.
 */
export function getFirebaseAuthErrorCode(input: unknown): string | null {
  return extractFirebaseCode(input);
}

/** Firebase Auth error code → analytics `failure_reason` (signup subset). */
const FIREBASE_FAILURE_REASON: Record<string, SignUpFailureReason> = {
  'auth/invalid-credential': 'invalid_credentials',
  'auth/invalid-login-credentials': 'invalid_credentials',
  'auth/wrong-password': 'invalid_credentials',
  'auth/invalid-email': 'invalid_credentials',
  'auth/email-already-in-use': 'duplicate_email',
  'auth/account-exists-with-different-credential': 'duplicate_email',
  'auth/weak-password': 'weak_password',
  'auth/popup-closed-by-user': 'cancelled',
  'auth/cancelled-popup-request': 'cancelled',
  'auth/network-request-failed': 'network_error',
};

/**
 * Classifies an auth error into the analytics `failure_reason` enum for
 * `sign_up_failed`. Unmapped or code-less errors fall back to `unknown`. The
 * server's "this email exists with a different password" arrives without a
 * Firebase code, so a caller that already knows the email is a duplicate should
 * pass `duplicate_email` explicitly rather than relying on this mapper.
 */
export function authFailureReason(input: unknown): SignUpFailureReason {
  const code = extractFirebaseCode(input);
  if (code && FIREBASE_FAILURE_REASON[code]) return FIREBASE_FAILURE_REASON[code];
  return 'unknown';
}

/**
 * Returns a user-facing message for an auth error. Handles `FirebaseError`
 * instances, plain `Error`s whose message contains a Firebase code, and
 * raw strings. Never returns a `Firebase: Error (...)` string.
 *
 * @param input - The error thrown, or an `error` string from a hook.
 * @param fallback - Optional fallback when no code can be extracted.
 */
export function getFriendlyAuthErrorMessage(input: unknown, fallback: string = DEFAULT_MESSAGE): string {
  const code = extractFirebaseCode(input);
  if (code && FIREBASE_MESSAGES[code]) return FIREBASE_MESSAGES[code];
  if (code) return fallback;

  // No Firebase code — trust an explicit non-Firebase error message if we have one.
  if (input instanceof Error && input.message && !input.message.startsWith('Firebase')) {
    return input.message;
  }
  if (typeof input === 'string' && input && !input.startsWith('Firebase')) {
    return input;
  }

  return fallback;
}
